import { Entity } from '../../../shared/models/Entity';
import { UserId } from '../value-objects/UserId';
import { SessionId } from '../value-objects/SessionId';

/**
 * 세션 엔티티
 * 사용자의 로그인 세션을 표현
 */
export class Session extends Entity<SessionId> {
  private readonly _userId: UserId;
  private readonly _token: string;
  private readonly _expiresAt: Date;
  private _isActive: boolean;
  private _lastAccessedAt?: Date;
  private readonly _deviceXCircle?: {
    userAgent: string;
    ip: string;
    deviceType?: string;
  };

  constructor(props: {
    id: SessionId;
    userId: UserId;
    token: string;
    expiresAt: Date;
    isActive?: boolean;
    lastAccessedAt?: Date;
    deviceXCircle?: {
      userAgent: string;
      ip: string;
      deviceType?: string;
    };
  }) {
    super(props.id);
    this._userId = props.userId;
    this._token = props.token;
    this._expiresAt = props.expiresAt;
    this._isActive = props.isActive ?? true;
    this._lastAccessedAt = props.lastAccessedAt;
    this._deviceXCircle = props.deviceXCircle;
  }

  // Getters
  get userId(): UserId {
    return this._userId;
  }

  get token(): string {
    return this._token;
  }

  get expiresAt(): Date {
    return this._expiresAt;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  get deviceXCircle() {
    return this._deviceXCircle;
  }

  get ipAddress(): string | undefined {
    return this._deviceXCircle?.ip;
  }

  get userAgent(): string | undefined {
    return this._deviceXCircle?.userAgent;
  }

  get lastAccessedAt(): Date | undefined {
    return this._lastAccessedAt;
  }

  /**
   * 세션 유효성 확인
   */
  isValid(): boolean {
    return this._isActive && !this.isExpired();
  }

  /**
   * 세션 만료 확인
   */
  isExpired(): boolean {
    return this._expiresAt < new Date();
  }

  /**
   * 세션 비활성화
   */
  invalidate(): void {
    this._isActive = false;
    this.touch();
  }

  /**
   * 남은 시간 계산 (밀리초)
   */
  getTimeRemaining(): number {
    const now = new Date().getTime();
    const expiry = this._expiresAt.getTime();
    return Math.max(0, expiry - now);
  }

  /**
   * 남은 시간 계산 (분)
   */
  getTimeRemainingInMinutes(): number {
    return Math.floor(this.getTimeRemaining() / 60000);
  }

  /**
   * JSON 변환
   */
  toJSON(): object {
    return {
      id: this._id.value,
      userId: this._userId.value,
      token: this._token,
      expiresAt: this._expiresAt.toISOString(),
      isActive: this._isActive,
      deviceXCircle: this._deviceXCircle,
      createdAt: this._createdAt.toISOString(),
      updatedAt: this._updatedAt.toISOString(),
    };
  }

  /**
   * 팩토리 메서드
   */
  static create(props: {
    userId: UserId;
    token: string;
    durationInMinutes?: number;
    deviceXCircle?: {
      userAgent: string;
      ip: string;
      deviceType?: string;
    };
  }): Session {
    const duration = props.durationInMinutes ?? 60; // 기본 60분
    const expiresAt = new Date();
    expiresAt.setMinutes(expiresAt.getMinutes() + duration);

    return new Session({
      id: SessionId.create(),
      userId: props.userId,
      token: props.token,
      expiresAt,
      deviceXCircle: props.deviceXCircle,
    });
  }
}